import {type MapResult, useAutoMapper} from '@igp/shared/auto-mapper';
import type {KeycloakLog} from '@igp/shared/contract/pam';
import type {UseListViewEntityProps, UseListViewEntityResult} from '@igp/shared/redux/view';
import {useViewInit} from '@redux';

import {
    keycloakLogEntityType,
    KeycloakLogQueryFieldsTypeName,
    KeycloakLogTypeName,
    KeycloakLogViewModelKeysTypeName,
    KeycloakLogViewModelTypeName,
} from './constants';
import type {
    KeycloakLogEntityType,
    KeycloakLogFilterKeys,
    KeycloakLogQueryFields,
    KeycloakLogViewModel,
    KeycloakLogViewModelKeys,
} from './types';

type UseKeycloakLogsProps = Omit<UseListViewEntityProps<KeycloakLogFilterKeys, KeycloakLogViewModelKeys>, 'ids' | 'defaultSorting'>;

type UseKeycloakLogsResult = Omit<
    UseListViewEntityResult<KeycloakLogViewModel, KeycloakLogViewModelKeys>,
    'requestStatus' | 'forceViewUpdate' | 'totalCount' | 'handleSortChange' | 'totalCount'
> & {count: number};

export function useKeycloakLogs({
    viewType,
    displayName,
    cleanDelay,
    isCancelable,
    fields,
    hiddenFilters,
    defaultFilters,
    defaultPaging,
    validateFilter,
    syncWithUrl,
}: UseKeycloakLogsProps): UseKeycloakLogsResult {
    const mapper = useAutoMapper();

    const queryFields = fields.map(f =>
        mapper.map<KeycloakLogViewModelKeys, KeycloakLogQueryFields>(f, KeycloakLogViewModelKeysTypeName, KeycloakLogQueryFieldsTypeName)
    );

    const {items, totalCount, viewEntity, searchFilter, handleFilterChange, handlePageChange, handlePageSizeChange} = useViewInit<
        KeycloakLog,
        KeycloakLogFilterKeys,
        KeycloakLogQueryFields
    >({
        viewType,
        displayName,
        cleanDelay,
        isCancelable,
        entity: {
            entity: keycloakLogEntityType satisfies KeycloakLogEntityType,
            fields: queryFields,
        },
        hiddenFilters,
        defaultFilters,
        defaultPaging,
        validateFilter,
        syncWithUrl,
    });

    const viewModelItems: MapResult<KeycloakLogViewModel>[] = items.map(item =>
        mapper.tryMap<KeycloakLog, KeycloakLogViewModel>(item, KeycloakLogTypeName, KeycloakLogViewModelTypeName)
    );

    return {
        count: totalCount,
        items: viewModelItems?.map(i => i.model),
        rawItems: viewModelItems?.map(i => ({isValid: i.isModelValid, raw: i.rawModelString})),
        filterString: viewEntity?.filter,
        searchFilter,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
    };
}
